import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';

const FAQsPage: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const faqs = [
        {
            question: "How is this different from MBTI, Gallup, or other personality tests?",
            answer: "Those tools tell you who you are. DoYourStep tells you where you're stuck right now and what to do about it. Personality types are static descriptions. This is a functional diagnosis of your current state - and it changes as you do."
        },
        {
            question: "How long does it take?",
            answer: "The diagnostic is 25 questions and takes about 8–10 minutes. Your full result - including your development profile and personalized protocol - is ready the moment you finish."
        },
        {
            question: "What are the five dimensions?",
            answer: "Inner Worth, Own Direction, Focus & Balance, Clear Thinking, and Real Connection. Your report scores all five, identifies the strongest blocking and growth patterns, and shows your main pendulum."
        },
        {
            question: "I already know my problems. How does this help?",
            answer: "Knowing the symptom (\"I can't start things\") is different from understanding the mechanism. The diagnostic shows you why - and that's where the protocol comes from. Generic advice doesn't work because it doesn't account for your specific pattern."
        },
        {
            question: "What science is this based on?",
            answer: "DoYourStep synthesizes work from Kahneman and Thaler (behavioral economics), Kegan (adult development), Ryan & Deci (Self-Determination Theory), Dweck (mindset), Csikszentmihalyi (Flow), ACT/CBT, emotion regulation, and related research."
        },
        {
            question: "What if my result seems wrong?",
            answer: "7-day full refund, no questions asked. That said, most people find the profile uncomfortably accurate."
        },
        {
            question: "Will the result change over time?",
            answer: "Yes - and that's the point. This isn't a fixed personality label. It reflects where you are now. We recommend retaking every 6–8 weeks to track what's improving and where to focus next."
        },
        {
            question: "What if I'm blocked in more than one dimension?",
            answer: "Most people are. The report scores all five and identifies your primary constraint - the one that, if improved, unlocks the others. You focus there first."
        },
        {
            question: "What do I actually receive after the diagnostic?",
            answer: "Your functional profile across all five dimensions, your primary blocker identified with a scientific explanation, a personalized protocol with specific micro-actions you can start today, and a baseline to track your progress over time."
        },
        {
            question: "What payment methods do you accept?",
            answer: "We accept all major credit and debit cards (Visa, Mastercard, American Express), as well as PayPal. All payments are processed securely through Polar."
        },
        {
            question: "Do you offer refunds?",
            answer: "Yes - 7-day no-questions guarantee. If the result feels inaccurate or unhelpful, just send us a message and we'll refund you. No forms, no hoops."
        },
        {
            question: "How can I contact support?",
            answer: "You can reach us through the Contact page, or by emailing support@doyourstep.com. We typically respond within 24 hours on business days."
        }
    ];

    const toggleFaq = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className="faqs-page">
            <Navigation />

            <section className="faqs-hero">
                <div className="faqs-hero__container">
                    <h1 className="faqs-hero__title">Common questions</h1>
                    <p className="faqs-hero__subtitle">
                        Everything you need to know about the DoYourStep diagnostic, the science behind it, and what you walk away with.
                    </p>
                </div>
            </section>

            <section className="faqs-content">
                <div className="faqs-content__container">
                    <div className="faqs-list">
                        {faqs.map((faq, index) => (
                            <div
                                key={faq.question}
                                className={`faq-item ${openIndex === index ? 'faq-item--open' : ''}`}
                            >
                                <button
                                    type="button"
                                    className="faq-item__question"
                                    onClick={() => toggleFaq(index)}
                                >
                                    <span>{faq.question}</span>
                                    <svg
                                        className="faq-item__chevron"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </button>
                                <div className="faq-item__answer-wrapper">
                                    <div className="faq-item__answer">
                                        <p>{faq.answer}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="faqs-cta">
                <div className="faqs-cta__container">
                    <h2 className="faqs-cta__title">Still have questions?</h2>
                    <p className="faqs-cta__text">
                        Our team is here to help. Or skip the questions and find out for yourself.
                    </p>
                    <div className="faqs-cta__buttons">
                        <Link to="/contact" className="faqs-cta__button faqs-cta__button--primary">
                            Contact Us
                        </Link>
                        <Link to="/quiz/doyourstep-quiz" className="faqs-cta__button faqs-cta__button--secondary">
                            Do your step &rarr;
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default FAQsPage;
